/**
 * PatrolCircle
 *
 * Renders a circle on the map showing a patrol's location radius.
 * Uses Google Maps Circle API directly since google-map-react doesn't have
 * a built-in Circle component.
 *
 * @see PatrolLocationCircle feature for showing patrol areas on dispatcher map
 */

import React, { useEffect, useRef, useCallback, memo } from 'react';
import {
  PatrolLocation,
  PATROL_CIRCLE_COLORS,
  PATROL_CIRCLE_RADIUS_METERS,
} from './types';

interface PatrolCircleProps {
  /** Patrol location data */
  patrol: PatrolLocation;
  /** Whether the circle should be visible */
  isVisible: boolean;
  /** Google Maps instance (from onGoogleApiLoaded) */
  map: google.maps.Map | null;
  /** Google Maps namespace (from onGoogleApiLoaded) */
  maps: typeof google.maps | null;
  /** Callback when circle is clicked */
  onClick?: (patrol: PatrolLocation) => void;
}

/**
 * PatrolCircle component manages a Google Maps Circle for displaying
 * a patrol's location radius on the map.
 *
 * The component handles:
 * - Creating the circle when map is available
 * - Updating style when patrol status changes
 * - Adding click listener for info popup
 * - Cleaning up the circle on unmount
 */
const PatrolCircle: React.FC<PatrolCircleProps> = ({
  patrol,
  isVisible,
  map,
  maps,
  onClick,
}) => {
  // Ref to store the circle instance
  const circleRef = useRef<google.maps.Circle | null>(null);
  // Ref to store click listener for cleanup
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // Memoize click handler to avoid unnecessary re-renders
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(patrol);
    }
  }, [onClick, patrol]);

  // Extract stable primitive values to avoid unnecessary effect triggers
  const { latitude, longitude, circleStatus, addressId } = patrol;

  // Create circle when map becomes available or addressId changes
  useEffect(() => {
    // Can't draw without map instance
    if (!map || !maps) {
      return;
    }

    // Get colors based on patrol circle status
    const colors = PATROL_CIRCLE_COLORS[circleStatus];

    // Circle options - increased zIndex for better click detection
    const circleOptions: google.maps.CircleOptions = {
      center: { lat: latitude, lng: longitude },
      radius: PATROL_CIRCLE_RADIUS_METERS,
      fillColor: colors.fill,
      fillOpacity: Math.max(colors.fillOpacity, 0.25),
      strokeColor: colors.stroke,
      strokeOpacity: colors.strokeOpacity,
      strokeWeight: 2,
      clickable: true,
      zIndex: 10,
    };

    // Create circle
    circleRef.current = new maps.Circle({
      ...circleOptions,
      map,
    });

    // Add click listener
    clickListenerRef.current = circleRef.current.addListener('click', handleClick);

    // Cleanup only on unmount or addressId change (not on every data refresh)
    return () => {
      if (circleRef.current) {
        if (clickListenerRef.current) {
          google.maps.event.removeListener(clickListenerRef.current);
          clickListenerRef.current = null;
        }
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    };
  }, [map, maps, addressId]); // Only recreate when map changes or different patrol

  // Update circle visibility
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.setVisible(isVisible);
    }
  }, [isVisible]);

  // Update circle position and style without recreating (prevents flashing)
  useEffect(() => {
    if (!circleRef.current) return;

    const colors = PATROL_CIRCLE_COLORS[circleStatus];
    circleRef.current.setCenter({ lat: latitude, lng: longitude });
    circleRef.current.setOptions({
      fillColor: colors.fill,
      fillOpacity: Math.max(colors.fillOpacity, 0.25),
      strokeColor: colors.stroke,
      strokeOpacity: colors.strokeOpacity,
    });
  }, [latitude, longitude, circleStatus]);

  // Update click handler when it changes (without recreating circle)
  useEffect(() => {
    if (circleRef.current && clickListenerRef.current) {
      // Remove old listener
      google.maps.event.removeListener(clickListenerRef.current);
      // Add new listener
      clickListenerRef.current = circleRef.current.addListener('click', handleClick);
    }
  }, [handleClick]);

  // This component doesn't render anything - it manages the circle imperatively
  return null;
};

/**
 * Custom comparison function for React.memo
 * Only re-render when values that actually affect the circle change
 */
function arePropsEqual(
  prevProps: PatrolCircleProps,
  nextProps: PatrolCircleProps
): boolean {
  // Always re-render if map instances change
  if (prevProps.map !== nextProps.map || prevProps.maps !== nextProps.maps) {
    return false;
  }

  // Compare visibility
  if (prevProps.isVisible !== nextProps.isVisible) {
    return false;
  }

  // Compare patrol data that affects rendering (by value, not reference)
  const prevPatrol = prevProps.patrol;
  const nextPatrol = nextProps.patrol;

  if (
    prevPatrol.addressId !== nextPatrol.addressId ||
    prevPatrol.latitude !== nextPatrol.latitude ||
    prevPatrol.longitude !== nextPatrol.longitude ||
    prevPatrol.circleStatus !== nextPatrol.circleStatus
  ) {
    return false;
  }

  // onClick callback reference doesn't matter for circle rendering
  // (we handle it via useCallback internally)

  return true;
}

export default memo(PatrolCircle, arePropsEqual);
